import * as React from 'react';
import { FC } from 'react';
import { classes, stylesheet } from 'typestyle';
import { BlackOpacity04, Gray100, TextInput, White, Gray10 } from '@traceair/tds';
import { ExpandMoreIcon } from '@traceair/webapp-icons';
import { sharedInvoicingCss } from '../../styles/shared';
import { SectionIcon } from '../../components/SectionHeader';
import { numericOnly } from '../../InvoicingUtils';

const css = sharedInvoicingCss;

const st = stylesheet({
  dateLabel: { flex: 1, minHeight: 24, alignContent: 'center' },
  categoryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '6px 16px 6px 56px',
    backgroundColor: White,
    borderBottom: `1px solid ${Gray10}`,
  },
  categoryRowAmount: {
    paddingRight: 24,
    height: 40,
  },
  categoryRowClickable: {
    cursor: 'pointer',
    $nest: { '&:hover': { backgroundColor: BlackOpacity04 } },
  },
  categoryRowStatic: {
    cursor: 'default',
  },
});

const Chevron: FC<{ isOpen: boolean }> = ({ isOpen }) => (
  <span style={{ display: 'flex', transform: isOpen ? 'none' : 'rotate(-90deg)', transition: 'transform 0.15s' }}>
    <ExpandMoreIcon size={12} color={Gray100} />
  </span>
);

// ─── TimeMaterialsDateHeader ──────────────────────────────────────────────────

type TimeMaterialsDateHeaderProps = {
  displayDate: string;
  total: number;
  isExpanded: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
};

export const TimeMaterialsDateHeader: FC<TimeMaterialsDateHeaderProps> = ({
  displayDate,
  total,
  isExpanded,
  onToggle,
  children,
}) => {
  const [rowHovered, setRowHovered] = React.useState(false);
  return (
    <div>
      <div
        className={css.sectionHeader}
        onClick={onToggle}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = BlackOpacity04;
          setRowHovered(true);
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '';
          setRowHovered(false);
        }}
      >
        <span className={css.sectionChevron}>
          <SectionIcon category="scan" isExpanded={isExpanded} rowHovered={rowHovered} />
        </span>
        <span className={classes(css.sectionTitle, st.dateLabel)}>{displayDate}</span>
        <span className={classes(css.sectionAmount, total === 0 && css.sectionAmountZero)}>
          ${total.toLocaleString()}
        </span>
      </div>
      {isExpanded && children}
    </div>
  );
};

// ─── TimeMaterialsCategoryHeader ──────────────────────────────────────────────

type TimeMaterialsCategoryHeaderBase = {
  title: string;
  subtitle?: string;
  isExpanded?: boolean;
  onToggle?: () => void;
  children?: React.ReactNode;
};

type TimeMaterialsCategoryHeaderWithInput = TimeMaterialsCategoryHeaderBase & {
  price: string;
  onPriceChange: (val: string) => void;
  amount?: never;
};

type TimeMaterialsCategoryHeaderWithAmount = TimeMaterialsCategoryHeaderBase & {
  amount: number;
  price?: never;
  onPriceChange?: never;
};

export type TimeMaterialsCategoryHeaderProps =
  | TimeMaterialsCategoryHeaderWithInput
  | TimeMaterialsCategoryHeaderWithAmount
  | (TimeMaterialsCategoryHeaderBase & { price?: never; onPriceChange?: never; amount?: never });

export const TimeMaterialsCategoryHeader: FC<TimeMaterialsCategoryHeaderProps> = ({
  title,
  subtitle,
  isExpanded,
  onToggle,
  children,
  price,
  onPriceChange,
  amount,
}) => (
  <>
    <div
      className={classes(
        st.categoryRow,
        price === undefined && st.categoryRowAmount,
        onToggle ? st.categoryRowClickable : st.categoryRowStatic,
      )}
      onClick={onToggle}
    >
      <div className={css.sectionTitleGroup}>
        <span className={css.sectionTitle}>{title}</span>
        {subtitle && (
          <span className={css.sectionSubtitle} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {subtitle}
            {onToggle && <Chevron isOpen={!!isExpanded} />}
          </span>
        )}
      </div>
      {price !== undefined ? (
        <div className={css.priceInputContainer} onClick={(e) => e.stopPropagation()}>
          <TextInput
            leftCell={price ? '$' : undefined}
            value={price}
            placeholder="Price"
            onChange={(val) => onPriceChange!(numericOnly(val))}
          />
        </div>
      ) : amount !== undefined ? (
        <span className={classes(css.sectionAmount, amount === 0 && css.sectionAmountZero)}>
          ${amount.toLocaleString()}
        </span>
      ) : null}
    </div>
    {isExpanded && children}
  </>
);
